import { Injectable } from '@angular/core';
import {
  GameEntityFullDTO,
  GameEntityId,
  GameEntityMinimalDTO,
  GameEntityType,
} from '../../../../../common/dto/game-entities.dto';
import { CoolHttp } from '@angular-cool/http';
import { ServerUrls } from '../../../../../common/dto/server-urls';
import { FileUploadRequestUtils } from '../utils/file-upload-request.utils';
import { PCacheable } from 'ts-cacheable';
import { MINUTE_IN_MILLISECONDS } from '../../../../../common/utils/date.utils';
import { Observable, Subject } from 'rxjs';
import { CacheState, CacheStateUpdater } from '@coool/cachestate';
import { filter } from 'rxjs/operators';
import { filterDefined } from '../utils/observable.utils';

const gameEntityCacheUpdater = new Subject<string | void>();

@Injectable()
export class GameEntitiesService {
  constructor(private _http: CoolHttp) {}

  @CacheState({
    maxAgeMS: 5 * MINUTE_IN_MILLISECONDS,
    cacheKey: {
      generator: args => `game-entity_${args[0]}`,
    },
    updatedObservable: gameEntityCacheUpdater.pipe(filterDefined()),
  })
  public getGameEntityById(gameEntityId: GameEntityId): Observable<GameEntityFullDTO> {
    return this._http.getObservable<GameEntityFullDTO>(
      ServerUrls.GameEntities.children.GetById.getAbsoluteUrlWithParams({
        [ServerUrls.GameEntities.children.GetById.params.gameEntityId]: gameEntityId,
      }),
    );
  }

  public async getGameEntitiesAsync(
    type: GameEntityType,
    skip: number,
    take: number,
    searchText: string | undefined,
  ): Promise<GameEntityMinimalDTO[]> {
    return await this._http.getAsync(
      ServerUrls.GameEntities.children.Get.getAbsoluteUrlWithQuery({
        [ServerUrls.GameEntities.children.Get.queryParams.type]: type,
        [ServerUrls.GameEntities.children.Get.queryParams.filter]: searchText,
        [ServerUrls.GameEntities.children.Get.queryParams.skip]: skip.toString(),
        [ServerUrls.GameEntities.children.Get.queryParams.take]: take.toString(),
      }),
    );
  }

  @CacheStateUpdater({
    updatedNotifier: gameEntityCacheUpdater,
    cacheKeyGenerator: args => {
      const gameEntity: GameEntityFullDTO = args[0];

      return gameEntity.id ? `game-entity_${gameEntity.id}` : undefined;
    },
  })
  public async upsertGameEntityAsync(gameEntity: GameEntityFullDTO): Promise<GameEntityFullDTO> {
    const formData = FileUploadRequestUtils.createRequestForm('entity', gameEntity, [
      {
        name: 'image',
        resolver: _ => _.image,
      },
    ]);

    return await this._http.postAsync(ServerUrls.GameEntities.children.Upsert.absoluteUrl, formData);
  }

  public async removeGameEntityAsync(gameEntityId: GameEntityId) {
    await this._http.deleteAsync(
      ServerUrls.GameEntities.children.DeleteById.getAbsoluteUrlWithParams({
        [ServerUrls.GameEntities.children.DeleteById.params.gameEntityId]: gameEntityId,
      }),
    );
  }

  @PCacheable({
    maxAge: 5 * MINUTE_IN_MILLISECONDS,
    cacheBusterObserver: gameEntityCacheUpdater,
  })
  public async getFieldNameSuggestionsAsyncCACHED(): Promise<string[]> {
    return await this._http.getAsync(ServerUrls.GameEntities.children.DocumentFieldNames.absoluteUrl);
  }
}
